import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Chip from "@mui/joy/Chip";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ColorPaletteProp } from "@mui/joy/styles";
import Typography from "@mui/joy/Typography";
import RateReviewIcon from '@mui/icons-material/RateReview';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface Props {
  data: any;
  rowsPerPage: number;
}

type SeverityType = "Low" | "Medium" | "High" | "Critical";

type StatusType = "In Review" | "Accepted" | "Rejected";

const PaginationTable: React.FC<Props> = ({ data, rowsPerPage }) => {
  const [currentPage, setCurrentPage] = React.useState(0);

  const SeverityColors: { [key in SeverityType]: ColorPaletteProp } = {
    Low: "success",
    Medium: "primary",
    High: "warning",
    Critical: "danger",
  };

  const StatusIcons: { [key in StatusType]: JSX.Element } = {
    "In Review": <RateReviewIcon />,
    Accepted: <CheckCircleIcon />,
    Rejected: <CancelIcon />,
  };

  const StatusColors: { [key in StatusType]: ColorPaletteProp } = {
    "In Review": "warning",
    Accepted: "success",
    Rejected: "danger",
  };


  const totalPages = Math.ceil(data.length / rowsPerPage);
  const indexOfLastRow = (currentPage + 1) * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const FirestoreTimestampToDate = ({
    seconds,
    nanoseconds,
  }: {
    seconds: any;
    nanoseconds: any;
  }) => {
    const milliseconds = seconds * 1000 + nanoseconds / 1000000;
    const date = new Date(milliseconds);
    const formattedDate = date.toLocaleDateString();
    return formattedDate;
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Report Title
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Event Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Severity Level
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Submission Date
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Score
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                {" "}
                Status{" "}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows &&
              currentRows.map((row: any, index: any) => (
                <tr key={row.id}>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">{index + 1}</Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.reporttitle ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">{row.eventName ?? "None"}</Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Chip
                      variant="soft"
                      size="sm"
                      color={
                        SeverityColors[
                          row.severitylevel as SeverityType
                        ] as ColorPaletteProp
                      }
                    >
                      {row.severitylevel}
                    </Chip>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {FirestoreTimestampToDate(row.submittedAt)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">{row.score}</Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Chip
                      variant="soft"
                      size="sm"
                      startDecorator={StatusIcons[row.status as StatusType]}
                      color={
                        StatusColors[
                          row.status as StatusType
                        ] as ColorPaletteProp
                      }
                    >
                      {row.status}
                    </Chip>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>
    </>
  );
};

export default PaginationTable;
